import React, { useEffect, useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useParams, useNavigate } from 'react-router-dom';
import { db, storage } from "../firebase";
import { getAuth, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from "../components/Navbar";

export function FeedbackSubmit() {
  const { projectName } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({ comment: '', rating: 5 });
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged(user => {
      if (user) setUser(user);
    });
    return () => unsubscribe();
  }, []);

  const handleGoogleLogin = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const auth = getAuth();
      const result = await signInWithPopup(auth, provider);
      setUser(result.user);
    } catch (error) {
      console.error(error);
    }
  };

  const handleFileChange = (e) => setFiles([...e.target.files]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return alert("Please log in with Google before submitting.");

    setLoading(true);
    try {
      const date = new Date().toISOString().split('T')[0];
      const newUrls = [];

      for (const file of files) {
        const storageRef = ref(storage, `feedback-images/${projectName}-${file.name}`);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        newUrls.push(url);
      }

      const docRef = doc(db, 'feedbacks', projectName);
      const docSnap = await getDoc(docRef);
      const existingImages = docSnap.exists() ? docSnap.data().images || [] : [];

      await setDoc(docRef, {
        clientName: user.displayName,
        email: user.email,
        photoURL: user.photoURL,
        rating: form.rating,
        comment: form.comment,
        date,
        images: [...existingImages, ...newUrls],
      }, { merge: true });

      setSubmitted(true);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleStarClick = (value) => {
    setForm((prev) => ({ ...prev, rating: value }));
  };

  return (
    <div className="min-h-screen bg-[#020617] text-slate-100 selection:bg-blue-500/10 overflow-x-hidden font-inter">
      <Navbar />

      {/* Background Graphics */}
      <div className="fixed inset-0 bg-grid opacity-10 pointer-events-none" />
      <div className="fixed top-0 right-0 w-[800px] h-[800px] bg-blue-500/5 blur-[120px] rounded-full -mr-48 -mt-48 animate-blob" />
      <div className="fixed bottom-0 left-0 w-[600px] h-[600px] bg-indigo-500/5 blur-[120px] rounded-full -ml-32 -mb-32 animate-blob delay-2000" />
      <div className="fixed inset-0 bg-mesh opacity-20 pointer-events-none" />

      <div className="container mx-auto max-w-2xl px-6 py-64 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="group glass-card p-16 md:p-20 rounded-[5rem] border border-white/80 shadow-2xl shadow-slate-200/50 relative overflow-hidden"
        >
          <AnimatePresence mode="wait">
            {submitted ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-10"
              >
                <div className="w-24 h-24 bg-green-500 text-white text-5xl flex items-center justify-center rounded-[2.5rem] mx-auto mb-12 shadow-2xl shadow-green-500/20">
                  ✓
                </div>
                <h2 className="text-5xl font-black text-white mb-10 tracking-tighter uppercase leading-none">Transmission <br /><span className="text-gradient">Verified.</span></h2>
                <p className="text-slate-400 text-lg font-medium lowercase tracking-tight mb-16 max-w-md mx-auto">
                  your architectural feedback has been securely synchronized with our engineering logs.
                </p>
                <button
                  onClick={() => navigate('/feedbacks')}
                  className="px-14 py-6 bg-slate-900 text-white font-black rounded-3xl hover:bg-black transition-all shadow-xl uppercase tracking-widest text-[11px]"
                >
                  VIEW LOGS
                </button>
              </motion.div>
            ) : (
              <motion.div key="form">
                <div className="text-center mb-16">
                  <h2 className="text-5xl font-black text-white mb-8 tracking-tighter uppercase leading-none">
                    Submit <br /><span className="text-gradient">Assessment.</span>
                  </h2>
                  <div className="h-1.5 w-24 bg-blue-600 mx-auto rounded-full shadow-lg shadow-blue-500/20" />
                </div>

                {!user ? (
                  <div className="text-center mb-16 p-12 bg-[#020617] border border-white/5 rounded-[3.5rem]">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.4em] mb-10 leading-none">Authentication Required</p>
                    <button
                      onClick={handleGoogleLogin}
                      className="group px-12 py-6 bg-slate-900/40 border border-white/10 text-white font-black rounded-[2.5rem] hover:bg-[#020617] transition-all flex items-center justify-center gap-4 mx-auto shadow-sm hover:scale-105 active:scale-95"
                    >
                      <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="w-6 h-6" />
                      <span className="uppercase tracking-widest text-xs">Authorize Identity</span>
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-8 mb-16 p-10 bg-[#020617] rounded-[3.5rem] border border-white/5 group-hover:bg-slate-900/40/50 transition-colors">
                    <div className="relative">
                      <div className="absolute -inset-2 bg-blue-500/10 blur-xl rounded-full" />
                      <img src={user.photoURL} className="w-20 h-20 rounded-3xl border border-white shadow-sm relative z-10" alt="User" />
                    </div>
                    <div className="space-y-1">
                      <p className="font-black text-white uppercase tracking-tighter text-xl leading-none">{user.displayName}</p>
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em]">{user.email}</p>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-16">
                  <div className="space-y-6 text-center">
                    <label className="text-[10px] uppercase font-black text-slate-400 tracking-[0.4em] block h-px">Efficiency Rating</label>
                    <div className="flex justify-center gap-4 pt-10">
                      {[1, 2, 3, 4, 5].map((value) => (
                        <div
                          key={value}
                          onClick={() => handleStarClick(value)}
                          className={`h-3 rounded-full cursor-pointer transition-all duration-700 ${value <= form.rating ? 'w-10 bg-blue-600 shadow-[0_0_20px_rgba(37,99,235,0.4)]' : 'w-3 bg-slate-100'}`}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <label className="text-[10px] uppercase font-black text-slate-400 tracking-[0.4em] ml-6">Executive Summary</label>
                    <textarea
                      name="comment"
                      placeholder="describe your assessment..."
                      rows={5}
                      onChange={(e) => setForm({ ...form, comment: e.target.value })}
                      className="w-full bg-[#020617] border border-white/5 rounded-[3rem] px-10 py-8 text-white focus:outline-none focus:border-blue-500 focus:bg-slate-900/40 transition-all font-medium resize-none placeholder:text-slate-300"
                    />
                  </div>

                  <div className="space-y-4">
                    <label className="text-[10px] uppercase font-black text-slate-400 tracking-[0.4em] ml-6">Technical Assets (Optional)</label>
                    <div className="relative">
                      <input
                        type="file"
                        multiple
                        onChange={handleFileChange}
                        className="w-full bg-[#020617] border border-white/5 rounded-[2.5rem] px-10 py-7 text-slate-400 focus:outline-none focus:border-blue-500 transition-all font-medium file:mr-6 file:py-2.5 file:px-8 file:rounded-full file:border-0 file:text-[10px] file:font-black file:bg-blue-600 file:text-white file:uppercase file:tracking-widest cursor-pointer"
                      />
                    </div>
                  </div>

                  <button
                    disabled={loading || !user}
                    className="group relative w-full py-7 bg-slate-900 text-white font-black rounded-[2.5rem] overflow-hidden transition-all shadow-2xl active:scale-95 disabled:opacity-50"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <span className="relative z-10 flex items-center justify-center gap-4 uppercase tracking-[0.2em] text-[11px]">
                      {loading ? 'SYNCHRONIZING...' : 'TRANSMIT ASSESSMENT'}
                      <span className="text-xl transition-transform group-hover:translate-x-1">→</span>
                    </span>
                  </button>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>

      <footer className="py-24 px-6 bg-slate-900/40 border-t border-white/5 text-center">
        <p className="text-[10px] font-black text-slate-300 uppercase tracking-[0.5em]">© 2024 LAB Developers — Quality Verification Loop.</p>
      </footer>
    </div>
  );
}
